import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useUpdatePaymentStatus } from "../../hooks/useUpdatePaymentStatus";

const PAYMENT_STATUSES = [
  { value: "Paid", label: "Paid" },
  { value: "Unpaid", label: "Unpaid" },
  { value: "Waived/Comp", label: "Waived/Comp" },
  { value: "Frozen1", label: "Frozen1 (30+ days past due)" },
  { value: "Frozen2", label: "Frozen2 (60+ days past due)" },
  { value: "Deactivated/Unsubscribed", label: "Deactivated/Unsubscribed" },
];

const STANDARD_STATUS = { value: "Standard", label: "Standard" };

const PaymentStatusModal = ({ show, onHide, companyId, currentStatus }) => {
  const [paymentStatus, setPaymentStatus] = useState(currentStatus || "");
  const [reason, setReason] = useState("");
  const [waivedEndDate, setWaivedEndDate] = useState("");

  const { mutate: updateStatus, isPending } = useUpdatePaymentStatus();

  // Get available statuses - include Standard only if current status is Waived/Comp
  const getAvailableStatuses = () => {
    const statuses = [...PAYMENT_STATUSES];
    if (currentStatus === "Waived/Comp") {
      statuses.push(STANDARD_STATUS);
    }
    return statuses;
  };

  useEffect(() => {
    if (paymentStatus === "Standard" && currentStatus !== "Waived/Comp") {
      setPaymentStatus("");
    }
  }, [currentStatus, paymentStatus]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!paymentStatus) {
      return;
    }

    const body = {
      paymentStatus,
      ...(reason && { reason }),
    };

    if (paymentStatus === "Waived/Comp") {
      body.waivedEndDate = waivedEndDate;
    }

    updateStatus(
      {
        companyId,
        body,
      },
      {
        onSuccess: () => {
          setPaymentStatus("");
          setReason("");
          setWaivedEndDate("");
          onHide();
        },
      }
    );
  };

  const handleClose = () => {
    setPaymentStatus(currentStatus || "");
    setReason("");
    setWaivedEndDate("");
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Update Payment Status</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Current Status</Form.Label>
            <Form.Control type="text" value={currentStatus || "N/A"} disabled />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>New Payment Status *</Form.Label>
            <Form.Select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              required
            >
              <option value="">Select status...</option>
              {getAvailableStatuses().map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          {paymentStatus === "Waived/Comp" && (
            <Form.Group className="mb-3">
              <Form.Label>Waived Until *</Form.Label>
              <Form.Control
                type="date"
                value={waivedEndDate}
                onChange={(e) => setWaivedEndDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Reason (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for status change"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit" 
            disabled={
              isPending || 
              !paymentStatus || 
              (paymentStatus === "Waived/Comp" && !waivedEndDate)
            }
          >
            {isPending ? "Updating..." : "Update Status"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PaymentStatusModal;

